import {ReplacementRequests} from '../../scheduling/ReplacementRequests'
import {MyUnionAwards} from '../../scheduling/UnionShiftAwards'
import { WorkerAssignmentReadinessPanel } from '../WorkerAssignmentReadiness'
import React, { useState } from 'react'
import { WorkerExternalJobBank } from '../WorkerExternalJobBank'
import { useI18n } from '../../../../i18n/I18nContext'
import { workerService } from '../../../../services/worker'
import { openDirections, hasDestination } from '../../../../services/mapsDirections'
import type { WorkerWorkspaceState } from '../useWorkerWorkspaceData'
import { WorkerEligibilitySummary } from '../WorkerPrimitives'
import { WorkerJobMap } from '../WorkerJobMap'
import { useAccessContext } from '../../../../context/AccessContext'
import { WorkerAssignmentMap } from '../WorkerAssignmentMap'
import {
  MobileCard,
  MobileChip,
  MobileEmptyState,
  MobilePrimaryButton,
  MobileSecondaryButton,
  MobileSectionTitle,
  statusTone,
} from './WorkerMobilePrimitives'

/**
 * Shifts — the web mirror of the Android "Shifts" tab: what is booked, what is
 * open in the marketplace, what has been applied for, and what has already
 * happened. Marketplace eligibility is rendered from the backend's own
 * requestability decision, never recomputed here.
 */

type ShiftsSegment = 'upcoming' | 'open' | 'applications' | 'history'

export const WorkerMobileShifts: React.FC<{ state: WorkerWorkspaceState }> = ({ state }) => {
  const { t, formatDateTime, formatTime } = useI18n()
  const { accessContext } = useAccessContext()
  const { data, busy, run } = state
  const [segment, setSegment] = useState<ShiftsSegment>('upcoming')
  const [openView, setOpenView] = useState<'list' | 'map'>('list')
  const jobMapEnabled = accessContext?.billingEntitlements?.features?.['workforce.job_map']?.enabled === true

  const segments: Array<{ id: ShiftsSegment; label: string; count: number }> = [
    { id: 'upcoming', label: t('Upcoming'), count: data.upcoming.length },
    { id: 'open', label: t('Find work'), count: data.shifts.length },
    { id: 'applications', label: t('Applied'), count: data.applications.length },
    { id: 'history', label: t('History'), count: data.history.length },
  ]

  return (
    <div className="wm-screen" data-testid="worker-mobile-shifts">
      <ReplacementRequests enabled={accessContext?.billingEntitlements?.features?.['workforce.replacements']?.enabled===true} assignments={data.upcoming}/>
      <MyUnionAwards enabled={accessContext?.billingEntitlements?.features?.['workforce.union_shift_allocation']?.enabled===true}/>
      {segment === 'open' ? <WorkerExternalJobBank applyNative={id => run(`apply-${id}`, () => workerService.apply(id), t('Application submitted.'))} /> : null}
      <div className="wm-segmented" role="tablist" aria-label={t('Shifts')}>
        {segments.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={segment === item.id}
            className="wm-segment"
            data-active={segment === item.id || undefined}
            onClick={() => setSegment(item.id)}
          >
            {item.label}
            {item.count ? <span className="wm-segment__count">{item.count}</span> : null}
          </button>
        ))}
      </div>

      {segment === 'upcoming' ? (
        data.upcoming.length ? data.upcoming.map((assignment) => (
          <MobileCard key={assignment.id} testId="worker-mobile-upcoming-card">
            <div className="wm-card__heading">
              <div>
                <small>{assignment.company_org_name || t('Assignment')}</small>
                <h3>{assignment.job_title || t('Assigned shift')}</h3>
                <p>{assignment.starts_at || assignment.startsAt
                  ? formatDateTime(String(assignment.startsAt || assignment.starts_at), { dateStyle: 'medium', timeStyle: 'short' })
                  : t('Time pending')}
                  {assignment.ends_at || assignment.endsAt ? ` – ${formatTime(String(assignment.endsAt || assignment.ends_at))}` : ''}</p>
              </div>
              <MobileChip tone={statusTone(assignment.assignmentStatus || assignment.assignment_status)}>
                {t(String(assignment.assignmentStatus || assignment.assignment_status || 'assigned').replace(/_/g, ' '))}
              </MobileChip>
            </div>
            <div className="wm-meta-row">
              <span>{t(String(assignment.workMode || assignment.work_mode || 'onsite'))}</span>
              <span>{assignment.location_name || t('Location pending')}</span>
              {assignment.payLabel || assignment.pay_label ? <span>{assignment.payLabel || assignment.pay_label}</span> : null}
            </div>
            {assignment.instructions ? <p className="wm-guidance">{assignment.instructions}</p> : null}
            <WorkerAssignmentReadinessPanel assignmentId={assignment.id} />
            {jobMapEnabled ? <WorkerAssignmentMap assignment={assignment} compact /> : null}
            {hasDestination({
              name: assignment.location_name,
              address: assignment.location_address,
              latitude: assignment.latitude,
              longitude: assignment.longitude,
            }) ? (
              <MobileSecondaryButton
                label={t('Directions')}
                onClick={() => openDirections({
                  name: assignment.location_name,
                  address: assignment.location_address,
                  latitude: assignment.latitude,
                  longitude: assignment.longitude,
                })}
              />
            ) : null}
          </MobileCard>
        )) : (
          <MobileEmptyState title={t('No upcoming shifts')} detail={t('Booked shifts appear here as soon as they are confirmed.')} />
        )
      ) : null}

      {segment === 'open' ? (
        <>
          {jobMapEnabled ? <div className="wm-segmented" role="tablist" aria-label={t('Job Bank view')}><button type="button" role="tab" aria-selected={openView === 'list'} className="wm-segment" data-active={openView === 'list' || undefined} onClick={() => setOpenView('list')}>{t('List')}</button><button type="button" role="tab" aria-selected={openView === 'map'} className="wm-segment" data-active={openView === 'map' || undefined} onClick={() => setOpenView('map')}>{t('Map')}</button></div> : null}
          {jobMapEnabled && openView === 'map' ? <WorkerJobMap compact shifts={data.shifts} busy={Boolean(busy)} onView={() => setOpenView('list')} onApply={(shift) => void run(`apply-${shift.id}`, () => workerService.apply(shift.id), t('Application submitted.'))} /> : <>
          {data.recommended.length ? (
            <MobileCard tone="info" testId="worker-mobile-recommended">
              <MobileSectionTitle title={t('Recommended for you')} detail={t('Matched by your organization from your skills and work area.')} />
              {data.recommended.map((shift: any) => (
                <div className="wm-inline-card" key={`recommended-${shift.id}`}>
                  <strong>{shift.job_title || t('Open shift')}</strong>
                  <small>{shift.location_name || t('Location pending')}</small>
                </div>
              ))}
            </MobileCard>
          ) : null}
          {data.shifts.length ? data.shifts.map((shift: any) => (
            <MobileCard key={shift.id} testId="worker-mobile-open-shift" id={`worker-shift-${shift.id}`}>
              <div className="wm-card__heading">
                <div>
                  <small>{shift.company_org_name || t('Open shift')}</small>
                  <h3>{shift.job_title || t('Open shift')}</h3>
                  <p>{shift.starts_at ? formatDateTime(shift.starts_at, { dateStyle: 'medium', timeStyle: 'short' }) : t('Time pending')}</p>
                </div>
                <MobileChip tone={shift.requestable === false ? 'warning' : 'positive'}>
                  {shift.requestable === false ? t('Not eligible') : t('Eligible')}
                </MobileChip>
              </div>
              <div className="wm-meta-row">
                <span>{shift.location_name || t('Location pending')}</span>
                {shift.workers_needed ? <span>{t('{count} needed', { count: String(shift.workers_needed) })}</span> : null}
              </div>
              <WorkerEligibilitySummary shift={shift} />
              <MobilePrimaryButton
                label={t('Apply')}
                testId="worker-mobile-apply"
                busy={busy === `apply-${shift.id}`}
                disabled={Boolean(busy) || shift.requestable === false}
                onClick={() => void run(`apply-${shift.id}`, () => workerService.apply(shift.id), t('Application submitted.'))}
              />
            </MobileCard>
          )) : (
            <MobileEmptyState title={t('No open shifts right now')} detail={t('New marketplace shifts appear here when your organization posts them.')} />
          )}
          </>}
        </>
      ) : null}

      {segment === 'applications' ? (
        data.applications.length ? data.applications.map((application: any) => (
          <MobileCard key={application.id} testId="worker-mobile-application">
            <div className="wm-card__heading">
              <div>
                <h3>{application.job_title || t('Shift application')}</h3>
                <p>{application.starts_at ? formatDateTime(application.starts_at, { dateStyle: 'medium', timeStyle: 'short' }) : t('Time pending')}</p>
              </div>
              <MobileChip tone={statusTone(application.status)}>
                {t(String(application.status || 'submitted').replace(/_/g, ' '))}
              </MobileChip>
            </div>
            <MobileSecondaryButton
              label={t('Withdraw')}
              disabled={Boolean(busy) || !['submitted', 'shortlisted'].includes(String(application.status || ''))}
              onClick={() => void run(`withdraw-${application.id}`, () => workerService.withdraw(application.id), t('Application withdrawn.'))}
            />
          </MobileCard>
        )) : (
          <MobileEmptyState title={t('No applications yet')} detail={t('Shifts you apply for appear here with their current status.')} />
        )
      ) : null}

      {segment === 'history' ? (
        data.history.length ? data.history.map((assignment) => (
          <MobileCard key={assignment.id} testId="worker-mobile-history">
            <div className="wm-card__heading">
              <div>
                <h3>{assignment.job_title || t('Completed shift')}</h3>
                <p>{assignment.starts_at || assignment.startsAt
                  ? formatDateTime(String(assignment.startsAt || assignment.starts_at), { dateStyle: 'medium', timeStyle: 'short' })
                  : t('Time pending')}</p>
                <small>{assignment.location_name || ''}</small>
              </div>
              <MobileChip tone={statusTone(assignment.assignmentStatus || assignment.assignment_status)}>
                {t(String(assignment.assignmentStatus || assignment.assignment_status || 'completed').replace(/_/g, ' '))}
              </MobileChip>
            </div>
          </MobileCard>
        )) : (
          <MobileEmptyState title={t('No shift history yet')} detail={t('Completed shifts appear here.')} />
        )
      ) : null}
    </div>
  )
}
